import express, { Request, Response } from 'express';
import cors from 'cors';
import axios, { AxiosRequestConfig } from 'axios';
import { ProxyConfig, StreamInfo } from './types';
import { Readable } from 'stream';

export class AudioProxyServer {
  private app: express.Application;
  private server: any;
  private config: Required<ProxyConfig>;

  constructor(config?: ProxyConfig) {
    this.config = {
      port: config?.port || 3001,
      host: config?.host || 'localhost',
      corsOrigins: config?.corsOrigins || '*',
      timeout: config?.timeout || 60000,
      maxRedirects: config?.maxRedirects || 20,
      userAgent: config?.userAgent || 'DesktopAudioProxy/1.0',
      enableLogging: config?.enableLogging ?? true,
      enableTranscoding: config?.enableTranscoding ?? false,
      cacheEnabled: config?.cacheEnabled ?? false,
      cacheTTL: config?.cacheTTL || 3600,
    };

    this.app = express();
    this.setupMiddleware();
    this.setupRoutes();
  }

  private setupMiddleware(): void {
    // CORS configuration
    const corsOptions = {
      origin: this.config.corsOrigins,
      credentials: true,
      methods: ['GET', 'POST', 'OPTIONS', 'HEAD'],
      allowedHeaders: ['Content-Type', 'Range', 'Accept-Encoding'],
      exposedHeaders: ['Content-Length', 'Content-Range', 'Accept-Ranges'],
    };

    this.app.use(cors(corsOptions));
    this.app.use(express.json());

    // Request logging
    if (this.config.enableLogging) {
      this.app.use((req, res, next) => {
        console.log(`[AudioProxy] ${req.method} ${req.path}`);
        next();
      });
    }
  }

  private setupRoutes(): void {
    // Health check endpoint
    this.app.get('/health', (req, res) => {
      res.json({
        status: 'ok',
        version: '1.0.0',
        uptime: process.uptime(),
        config: {
          port: this.config.port,
          enableTranscoding: this.config.enableTranscoding,
          cacheEnabled: this.config.cacheEnabled,
        },
      });
    });

    // Stream info endpoint
    this.app.get('/info', async (req, res) => {
      const url = req.query.url as string;
      if (!url) {
        res.status(400).json({ error: 'URL parameter required' });
        return;
      }

      try {
        const info = await this.getStreamInfo(url);
        res.json(info);
      } catch (error: any) {
        res.status(500).json({ error: error.message });
      }
    });

    // Main proxy endpoint
    this.app.get('/proxy', this.handleProxy.bind(this));

    // Handle OPTIONS for preflight
    this.app.options('/proxy', cors());
  }

  private async handleProxy(req: Request, res: Response): Promise<void> {
    const url = req.query.url as string;

    if (!url) {
      res.status(400).json({ error: 'URL parameter required' });
      return;
    }

    try {
      if (this.config.enableLogging) {
        console.log(`[AudioProxy] Proxying: ${url}`);
      }

      const config: AxiosRequestConfig = {
        method: 'GET',
        url: url,
        responseType: 'stream',
        headers: {
          'User-Agent': this.config.userAgent,
          'Accept': 'audio/*, application/octet-stream',
          'Accept-Encoding': 'identity',
          ...(req.headers.range ? { Range: req.headers.range } : {}),
        },
        maxRedirects: this.config.maxRedirects,
        timeout: this.config.timeout,
        decompress: false,
        validateStatus: (status) => status >= 200 && status < 400,
      };

      const response = await axios(config);

      // Set response headers
      const headers: any = {
        'Content-Type': response.headers['content-type'] || 'audio/mpeg',
        'Cache-Control': this.config.cacheEnabled 
          ? `public, max-age=${this.config.cacheTTL}` 
          : 'no-cache, no-store',
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'GET, OPTIONS, HEAD',
        'Access-Control-Allow-Headers': 'Content-Type, Range, Accept-Encoding',
        'Access-Control-Expose-Headers': 'Content-Length, Content-Range, Accept-Ranges',
      };

      // Forward important headers
      const forwardHeaders = [
        'content-length',
        'content-range',
        'accept-ranges',
        'etag',
        'last-modified',
      ];

      forwardHeaders.forEach(header => {
        if (response.headers[header]) {
          headers[header.split('-').map(part => 
            part.charAt(0).toUpperCase() + part.slice(1)
          ).join('-')] = response.headers[header];
        }
      });

      res.status(response.status).set(headers);

      // Stream the response
      const stream = response.data as Readable;
      stream.pipe(res);

      stream.on('error', (error) => {
        if (this.config.enableLogging) {
          console.error('[AudioProxy] Stream error:', error);
        }
        if (!res.headersSent) {
          res.status(500).json({ error: 'Stream error' });
        }
      });

      stream.on('end', () => {
        if (this.config.enableLogging) {
          console.log('[AudioProxy] Stream completed');
        }
      });

    } catch (error: any) {
      if (this.config.enableLogging) {
        console.error('[AudioProxy] Error:', error.message);
      }

      if (error.response) {
        res.status(error.response.status).json({
          error: `Target server returned ${error.response.status}`,
          message: error.message,
        });
      } else {
        res.status(500).json({
          error: 'Proxy error',
          message: error.message,
        });
      }
    }
  }

  private async getStreamInfo(url: string): Promise<StreamInfo> {
    try {
      const response = await axios.head(url, {
        headers: {
          'User-Agent': this.config.userAgent,
        },
        maxRedirects: this.config.maxRedirects,
        timeout: 10000,
      });

      return {
        url: response.config.url || url,
        contentType: response.headers['content-type'],
        contentLength: parseInt(response.headers['content-length'] || '0'),
        status: response.status,
        headers: response.headers as Record<string, string>,
        canPlay: true,
        requiresProxy: true,
      };
    } catch (error: any) {
      throw new Error(`Failed to get stream info: ${error.message}`);
    }
  }

  public async start(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.server = this.app.listen(this.config.port, this.config.host, () => {
          console.log(`🎵 Desktop Audio Proxy running on http://${this.config.host}:${this.config.port}`);
          console.log(`📡 Use http://${this.config.host}:${this.config.port}/proxy?url=YOUR_AUDIO_URL`);
          resolve();
        });

        this.server.on('error', (error: any) => {
          if (error.code === 'EADDRINUSE') {
            console.error(`Port ${this.config.port} is already in use`);
          }
          reject(error);
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  public async stop(): Promise<void> {
    return new Promise((resolve) => {
      if (this.server) {
        this.server.close(() => {
          console.log('🛑 Desktop Audio Proxy stopped');
          resolve();
        });
      } else {
        resolve();
      }
    });
  }

  public getInfo(): { port: number; host: string; isRunning: boolean } {
    return {
      port: this.config.port,
      host: this.config.host,
      isRunning: !!this.server && this.server.listening,
    };
  }
}

// Convenience functions
export function createProxyServer(config?: ProxyConfig): AudioProxyServer {
  return new AudioProxyServer(config);
}

export async function startProxyServer(config?: ProxyConfig): Promise<AudioProxyServer> {
  const server = createProxyServer(config);
  await server.start();
  return server;
}